'use client'

import { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { ChatMockup, Message } from '@/components/ui/chat-mockup'
import { Calendar, MessageCircle, UserPlus, ChevronRight, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { STARTING_PRICE_NOTE, STARTING_PRICE_TEXT } from '@/config/pricing'

interface Scenario {
  id: string
  label: string
  icon: LucideIcon
  color: string
  description: string
  messages: Message[]
}

const scenarios: Scenario[] = [
  {
    id: 'appointments',
    label: 'Agendar Cita',
    icon: Calendar,
    color: 'bg-blue-500',
    description: 'El agente verifica disponibilidad y agenda automáticamente en tu calendario.',
    messages: [
      {
        id: '1',
        text: 'Hola, quisiera agendar una cita para esta semana',
        sender: 'user',
        time: '10:30',
      },
      {
        id: '2',
        text: (
          <>
            ¡Hola! 👋 Con gusto te ayudo. Tengo estos horarios disponibles:
            <br />
            <br />
            📅 <strong>Jueves 24</strong>: 10:00 AM, 4:00 PM
            <br />
            📅 <strong>Viernes 25</strong>: 11:00 AM, 2:00 PM
            <br />
            <br />
            ¿Cuál prefieres?
          </>
        ),
        sender: 'agent',
        time: '10:30',
      },
      { id: '3', text: 'El viernes a las 2pm está bien', sender: 'user', time: '10:31' },
      {
        id: '4',
        text: (
          <>
            ¡Listo! ✅ Quedó agendada tu cita para el <strong>Viernes 25 a las 2:00 PM</strong>.
            <br />
            <br />
            Te envié una invitación a tu correo. ¡Nos vemos!
          </>
        ),
        sender: 'agent',
        time: '10:31',
      },
    ],
  },
  {
    id: 'faq',
    label: 'Info de Negocio',
    icon: MessageCircle,
    color: 'bg-emerald-500',
    description: 'Responde preguntas frecuentes al instante usando la información de tu empresa.',
    messages: [
      {
        id: '1',
        text: '¿Tienen estacionamiento? ¿Y cuáles son sus precios?',
        sender: 'user',
        time: '14:15',
      },
      {
        id: '2',
        text: (
          <>
            Sí, contamos con estacionamiento gratuito para clientes 🚗.
            <br />
            <br />
            Sobre los precios, nuestros planes comienzan desde{' '}
            <strong>{STARTING_PRICE_TEXT}</strong>
            {STARTING_PRICE_NOTE ? ` (${STARTING_PRICE_NOTE})` : ''}. Puedes ver todos los detalles
            aquí: <Link href="/precios">luckagents.com/precios</Link>
          </>
        ),
        sender: 'agent',
        time: '14:15',
      },
      { id: '3', text: 'Perfecto, gracias', sender: 'user', time: '14:16' },
      {
        id: '4',
        text: '¡De nada! Si tienes más dudas, aquí estoy. 😊',
        sender: 'agent',
        time: '14:16',
      },
    ],
  },
  {
    id: 'leads',
    label: 'Captura Leads',
    icon: UserPlus,
    color: 'bg-cyan-500',
    description: 'Califica prospectos y guarda sus datos antes de pasar con un humano.',
    messages: [
      {
        id: '1',
        text: 'Me interesa una cotización para mi empresa',
        sender: 'user',
        time: '09:10',
      },
      {
        id: '2',
        text: (
          <>
            ¡Genial! Para darte la mejor opción, necesito unos datos rápidos. 📋
            <br />
            <br />
            ¿Cuál es el nombre de tu empresa?
          </>
        ),
        sender: 'agent',
        time: '09:10',
      },
      { id: '3', text: 'TechSolutions SA', sender: 'user', time: '09:11' },
      {
        id: '4',
        text: 'Gracias. ¿Y cuántos empleados tienen aproximadamente?',
        sender: 'agent',
        time: '09:11',
      },
      { id: '5', text: 'Somos unos 50', sender: 'user', time: '09:12' },
      {
        id: '6',
        text: '¡Perfecto! Un ejecutivo especializado en PyMEs te contactará en breve con una propuesta a medida. 🚀',
        sender: 'agent',
        time: '09:12',
      },
    ],
  },
]

export function ConversationExamplesInteractive() {
  const [activeScenario, setActiveScenario] = useState<string>(scenarios[0].id)
  const shouldReduceMotion = useReducedMotion()

  const activeData = scenarios.find((s) => s.id === activeScenario) || scenarios[0]

  return (
    <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
      {/* Left: Controls */}
      <div className="space-y-4">
        {scenarios.map((scenario) => {
          const isActive = activeScenario === scenario.id
          return (
            <button
              key={scenario.id}
              type="button"
              onClick={() => setActiveScenario(scenario.id)}
              className={cn(
                'focus-visible:outline-primary/30 group relative w-full overflow-hidden rounded-2xl border p-6 text-left transition duration-300 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2',
                isActive
                  ? 'scale-[1.02] border-black/10 bg-white shadow-lg dark:border-white/10 dark:bg-white/5'
                  : 'border-transparent bg-transparent hover:bg-black/5 dark:hover:bg-white/5'
              )}
              aria-pressed={isActive}
            >
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'rounded-xl p-3 text-white transition-colors',
                    scenario.color,
                    !isActive &&
                      'opacity-70 grayscale group-hover:opacity-100 group-hover:grayscale-0'
                  )}
                >
                  <scenario.icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div>
                  <h3
                    className={cn(
                      'mb-1 text-lg font-bold transition-colors',
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  >
                    {scenario.label}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {scenario.description}
                  </p>
                </div>
                {isActive && (
                  <ChevronRight
                    className="text-primary absolute right-6 top-1/2 h-5 w-5 -translate-y-1/2"
                    aria-hidden="true"
                  />
                )}
              </div>
            </button>
          )
        })}
      </div>

      {/* Right: Phone Mockup */}
      <div className="relative flex justify-center lg:justify-end">
        {/* Blobs */}
        <div className="absolute left-1/2 top-1/2 h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/20 blur-[60px] md:h-[300px] md:w-[300px] md:blur-[100px]" />

        {shouldReduceMotion ? (
          <div key={activeScenario} className="relative z-10">
            <ChatMockup
              messages={activeData.messages}
              className="mx-auto w-full max-w-[380px] shadow-2xl"
            />
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeScenario}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              <ChatMockup
                messages={activeData.messages}
                className="mx-auto w-full max-w-[380px] shadow-2xl"
              />
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}
